import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Section from '../components/ui/Section';
import SEOHelmet from '../seo/SEOHelmet';
import HeaderBanner from '../components/ui/HeaderBanner';
import { useLanguage } from '../i18n/LanguageContext';

const ContentContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const Title = styled(motion.h1)`
  font-size: 2.8rem;
  margin-bottom: 3rem;
  text-align: center;
  position: relative;
  display: inline-block;
  left: 50%;
  transform: translateX(-50%);
  
  &:after {
    content: '';
    position: absolute;
    bottom: -20px;
    left: 0;
    width: 100px;
    height: 3px;
    background: linear-gradient(90deg, var(--accent-primary), transparent);
    left: 50%;
    transform: translateX(-50%);
  }
`;

const Heading = styled(motion.h2)`
  font-size: 1.8rem;
  margin: 2.5rem 0 1.5rem;
  position: relative;
  padding-bottom: 0.5rem;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 60px;
    height: 2px;
    background-color: var(--accent-primary);
  }
`;

const SubHeading = styled(motion.h3)`
  font-size: 1.4rem;
  margin: 1.5rem 0 1rem;
  color: var(--text-primary);
`;

const Paragraph = styled(motion.p)`
  margin-bottom: 1.5rem;
  line-height: 1.8;
  color: var(--text-secondary);
  font-size: 1.05rem;
`;

const List = styled(motion.ul)`
  margin-bottom: 1.5rem;
  padding-left: 1.5rem;
`;

const ListItem = styled(motion.li)`
  margin-bottom: 0.8rem;
  line-height: 1.6;
  color: var(--text-secondary);
`;

const LastUpdated = styled.p`
  margin-top: 4rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  color: var(--text-secondary);
  font-style: italic;
  font-size: 0.9rem;
`;

const TermsOfService: React.FC = () => {
  const { language } = useLanguage();
  
  const isItalian = language === 'it';
  const lastUpdated = isItalian ? 'Ultimo aggiornamento: 20 Luglio 2023' : 'Last updated: July 20, 2023';
  
  return (
    <>
      <SEOHelmet
        title={isItalian ? "Termini di Servizio - J-O-H" : "Terms of Service - J-O-H"}
        description={isItalian 
          ? "Termini e condizioni per l'utilizzo dei servizi di J-O-H Gestione Immobiliare di Lusso." 
          : "Terms and conditions for using J-O-H Luxury Property Management services."}
        canonical="/terms-of-service"
      />
      
      <HeaderBanner />
      
      <Section padding="5rem 0">
        <ContentContainer>
          <Title
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {isItalian ? "Termini di Servizio" : "Terms of Service"}
          </Title>
          
          <Paragraph>
            {isItalian 
              ? "Benvenuto nei Termini di Servizio di J-O-H Gestione Immobiliare di Lusso. I seguenti termini e condizioni regolano l'utilizzo del nostro sito web e dei servizi offerti."
              : "Welcome to the Terms of Service of J-O-H Luxury Property Management. The following terms and conditions govern your use of our website and services offered."}
          </Paragraph>
          
          <Heading>
            {isItalian ? "1. Accettazione dei Termini" : "1. Acceptance of Terms"}
          </Heading>
          <Paragraph>
            {isItalian 
              ? "Utilizzando il nostro sito web e i nostri servizi, accetti di essere vincolato da questi Termini di Servizio. Se non sei d'accordo con parte o tutti i termini di questo accordo, non puoi utilizzare i nostri servizi."
              : "By using our website and services, you agree to be bound by these Terms of Service. If you disagree with part or all of the terms of this agreement, you may not use our services."}
          </Paragraph>
          
          <Heading>
            {isItalian ? "2. Descrizione dei Servizi" : "2. Description of Services"}
          </Heading>
          <Paragraph>
            {isItalian 
              ? "J-O-H offre servizi di gestione immobiliare di lusso, inclusi ma non limitati a affitti a breve termine, gestione proprietà, consulenza immobiliare e servizi di concierge."
              : "J-O-H provides luxury property management services, including but not limited to short-term rentals, property management, real estate consulting, and concierge services."}
          </Paragraph>
          
          <Heading>
            {isItalian ? "3. Prenotazioni e Pagamenti" : "3. Bookings and Payments"}
          </Heading>
          <Paragraph>
            {isItalian 
              ? "Per effettuare una prenotazione, è necessario fornire informazioni accurate e complete. I pagamenti devono essere effettuati secondo i termini specificati al momento della prenotazione. Le tariffe possono variare in base alla stagione e alla disponibilità."
              : "To make a booking, you must provide accurate and complete information. Payments must be made according to the terms specified at the time of booking. Rates may vary based on season and availability."}
          </Paragraph>
          
          <SubHeading>
            {isItalian ? "3.1 Politica di Cancellazione" : "3.1 Cancellation Policy"}
          </SubHeading>
          <Paragraph>
            {isItalian 
              ? "Le nostre politiche di cancellazione variano in base alla proprietà e al periodo di prenotazione. Ti preghiamo di consultare i termini specifici indicati durante il processo di prenotazione."
              : "Our cancellation policies vary based on the property and booking period. Please refer to the specific terms indicated during the booking process."}
          </Paragraph>
          
          <Heading>
            {isItalian ? "4. Comportamento dell'Ospite" : "4. Guest Conduct"}
          </Heading>
          <Paragraph>
            {isItalian 
              ? "Gli ospiti sono tenuti a rispettare le proprietà e le regole della casa. Qualsiasi danno causato dall'ospite sarà addebitato. Ci riserviamo il diritto di terminare il soggiorno senza rimborso in caso di comportamento inappropriato."
              : "Guests are expected to respect the properties and house rules. Any damage caused by the guest will be charged. We reserve the right to terminate the stay without refund in case of inappropriate behavior."}
          </Paragraph>
          
          <Heading>
            {isItalian ? "5. Proprietà Intellettuale" : "5. Intellectual Property"}
          </Heading>
          <Paragraph>
            {isItalian 
              ? "Tutti i contenuti presenti sul nostro sito web, inclusi testi, grafica, logo, immagini e software, sono di proprietà di J-O-H o dei suoi fornitori di contenuti e sono protetti dalle leggi sul copyright."
              : "All content on our website, including text, graphics, logos, images, and software, is the property of J-O-H or its content suppliers and is protected by copyright laws."}
          </Paragraph>
          
          <Heading>
            {isItalian ? "6. Limitazioni di Responsabilità" : "6. Limitations of Liability"}
          </Heading>
          <Paragraph>
            {isItalian 
              ? "J-O-H non sarà responsabile per eventuali danni diretti, indiretti, incidentali o consequenziali derivanti dall'utilizzo o dall'impossibilità di utilizzare i nostri servizi."
              : "J-O-H will not be liable for any direct, indirect, incidental, or consequential damages arising from the use or inability to use our services."}
          </Paragraph>
          
          <Heading>
            {isItalian ? "7. Modifiche ai Termini" : "7. Changes to Terms"}
          </Heading>
          <Paragraph>
            {isItalian 
              ? "Ci riserviamo il diritto di modificare questi Termini di Servizio in qualsiasi momento. Le modifiche entreranno in vigore immediatamente dopo la pubblicazione sul nostro sito web. L'uso continuato dei nostri servizi dopo tali modifiche costituirà il tuo consenso ai nuovi termini."
              : "We reserve the right to modify these Terms of Service at any time. Changes will be effective immediately upon posting on our website. Your continued use of our services after such modifications will constitute your consent to the new terms."}
          </Paragraph>
          
          <Heading>
            {isItalian ? "8. Legge Applicabile" : "8. Governing Law"}
          </Heading>
          <Paragraph>
            {isItalian 
              ? "Questi Termini di Servizio sono regolati e interpretati in conformità con le leggi italiane. Qualsiasi controversia derivante da o relativa a questi termini sarà soggetta alla giurisdizione esclusiva dei tribunali di Firenze, Italia."
              : "These Terms of Service are governed by and construed in accordance with the laws of Italy. Any dispute arising out of or relating to these terms will be subject to the exclusive jurisdiction of the courts of Florence, Italy."}
          </Paragraph>
          
          <Heading>
            {isItalian ? "9. Contattaci" : "9. Contact Us"}
          </Heading>
          <Paragraph>
            {isItalian 
              ? "Per qualsiasi domanda riguardante questi Termini di Servizio, ti preghiamo di contattarci tramite la nostra pagina di contatto."
              : "For any questions regarding these Terms of Service, please contact us through our contact page."}
          </Paragraph>
          
          <LastUpdated>{lastUpdated}</LastUpdated>
        </ContentContainer>
      </Section>
    </>
  );
};

export default TermsOfService; 